import React, { useMemo } from 'react';
import { Box, Chip, Typography, alpha } from '@mui/material';
import ChannelImage from './ChannelImage';

const DEFAULT_THEME = '#4ce1b6';

/**
 * Shared Channels picker — All Channels is the only OTA control.
 *
 * Rate / sync drawers (otaMode="display"):
 *   - Individual OTA chips are display-only (not clickable)
 *   - PMS is optional (pmsMode="toggle") — click to include/exclude independently
 *   - Default selection should be ['pms','all'] or ['all']
 *
 * Inventory restrictions (otaMode="selectable"):
 *   - Individual OTAs can be toggled; selecting all promotes to All Channels
 */
export default function AllChannelsSelector({
  channels = [],
  value = [],
  onChange,
  allowPms = true,
  /** 'toggle' = PMS independent (default); 'mirror' = PMS follows All Channels */
  pmsMode = 'toggle',
  otaMode = 'display', // 'display' | 'selectable'
  themeColor = DEFAULT_THEME,
  disabled = false,
  helperText = null,
  channelMessages = null,
}) {
  const selected = Array.isArray(value) ? value : [];
  const channelsArray = useMemo(
    () => (Array.isArray(channels) ? channels : []),
    [channels]
  );
  const hasOtaChannels = channelsArray.length > 0;
  const allOn = selected.includes('all');
  const pmsOn = pmsMode === 'mirror' ? allOn && allowPms : selected.includes('pms');

  const otaIds = useMemo(
    () => channelsArray.map((ch) => String(ch.id)),
    [channelsArray]
  );

  const chipSx = (filled, { clickable = true } = {}) => ({
    cursor: disabled || !clickable ? 'default' : 'pointer',
    pointerEvents: disabled || !clickable ? 'none' : 'auto',
    fontWeight: filled ? 700 : 500,
    backgroundColor: filled ? themeColor : 'transparent',
    color: filled ? '#000' : 'inherit',
    borderColor: filled ? themeColor : alpha(themeColor, 0.45),
    borderWidth: filled ? 1.5 : 1,
    opacity: disabled ? 0.6 : filled ? 1 : 0.85,
    '&:hover':
      disabled || !clickable
        ? undefined
        : {
            backgroundColor: filled ? themeColor : alpha(themeColor, 0.08),
            borderColor: themeColor,
          },
  });

  const setValue = (next) => {
    if (disabled || typeof onChange !== 'function') return;
    onChange(next);
  };

  const toggleAll = () => {
    if (allOn) {
      // Turning off All Channels clears OTA (+ mirrored PMS)
      if (pmsMode === 'mirror') {
        setValue([]);
      } else {
        setValue(selected.filter((id) => id !== 'all'));
      }
    } else if (pmsMode === 'mirror' && allowPms) {
      setValue(['pms', 'all']);
    } else if (allowPms && selected.includes('pms')) {
      setValue(['pms', 'all']);
    } else {
      setValue(['all']);
    }
  };

  const togglePms = () => {
    if (!allowPms || pmsMode === 'mirror') return;
    if (selected.includes('pms')) {
      setValue(selected.filter((id) => id !== 'pms'));
    } else {
      setValue([...selected, 'pms']);
    }
  };

  const toggleOta = (channelIdStr) => {
    if (otaMode !== 'selectable') return;
    if (allOn) {
      const next = otaIds.filter((id) => id !== channelIdStr);
      const keepPms =
        allowPms && pmsMode === 'toggle' && selected.includes('pms') ? ['pms'] : [];
      setValue([...keepPms, ...next]);
      return;
    }
    if (selected.includes(channelIdStr)) {
      setValue(selected.filter((id) => id !== channelIdStr));
    } else {
      const next = [...selected, channelIdStr];
      const selectedOtas = next.filter((id) => id !== 'pms' && id !== 'all');
      if (otaIds.length > 0 && selectedOtas.length >= otaIds.length) {
        const keepPms =
          allowPms && (pmsMode === 'mirror' || next.includes('pms')) ? ['pms'] : [];
        setValue(allowPms && pmsMode === 'mirror' ? ['pms', 'all'] : [...keepPms, 'all']);
      } else {
        setValue(next);
      }
    }
  };

  const otaFilled = (channelIdStr) => {
    if (allOn) return true;
    if (otaMode === 'display') return false;
    return selected.includes(channelIdStr);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0 }}>
          Channels
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasOtaChannels ? (
            <Chip
              label={
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: allOn ? 700 : 500,
                    color: allOn ? '#000' : 'inherit',
                  }}
                >
                  All Channels
                </Box>
              }
              onClick={toggleAll}
              variant="outlined"
              sx={chipSx(allOn, { clickable: true })}
            />
          ) : null}
          {channelMessages?.all ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {channelMessages.all}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: helperText ? 1 : 0 }}>
        {allowPms && (
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ChannelImage isPMS={true} size={20} />
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: pmsOn ? 700 : 500,
                    color: pmsOn ? '#000' : 'inherit',
                  }}
                >
                  PMS
                </Box>
              </Box>
            }
            onClick={pmsMode === 'toggle' ? togglePms : undefined}
            variant="outlined"
            sx={chipSx(pmsOn, { clickable: pmsMode === 'toggle' })}
          />
        )}

        {channelsArray.map((channel) => {
          const idStr = String(channel.id);
          const filled = otaFilled(idStr);
          const selectable = otaMode === 'selectable';
          return (
            <Chip
              key={channel.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ChannelImage channel={channel} size={20} />
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: filled ? 700 : 500,
                      color: filled ? '#000' : 'inherit',
                    }}
                  >
                    {channel.name}
                  </Box>
                </Box>
              }
              onClick={selectable ? () => toggleOta(idStr) : undefined}
              variant="outlined"
              sx={chipSx(filled, { clickable: selectable })}
            />
          );
        })}
      </Box>

      {helperText ? (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Expand 'all' into concrete OTA channel id strings (keeps 'pms' if present). */
export function expandChannelSelection(selectedChannels, channels) {
  const selected = Array.isArray(selectedChannels) ? [...selectedChannels] : [];
  const channelsArray = Array.isArray(channels) ? channels : [];
  if (!selected.includes('all')) return selected;
  const hasPms = selected.includes('pms');
  return [
    ...(hasPms ? ['pms'] : []),
    ...channelsArray.map((ch) => String(ch.id)),
  ];
}

/** True when selection targets OTAs (All Channels or any OTA id). */
export function selectionTargetsOtas(selectedChannels) {
  const selected = Array.isArray(selectedChannels) ? selectedChannels : [];
  return selected.some((id) => id === 'all' || (id !== 'pms' && id != null && id !== ''));
}

/** Default rate-drawer selection: PMS + All Channels. */
export const DEFAULT_RATE_CHANNEL_SELECTION = ['pms', 'all'];

/**
 * Selection for an existing inventory block: 'pms' only when include_pms is set,
 * 'all' when the block targets any OTA channels (display mode cannot show subsets).
 */
export function deriveBlockChannelSelection(block) {
  const selection = [];
  if (block?.include_pms) selection.push('pms');
  if (Array.isArray(block?.channel_ids) && block.channel_ids.length > 0) {
    selection.push('all');
  }
  return selection;
}
